import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import SubscriptionPlanForm from './subscription-plan-form';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { SubscriptionPlan } from '@/models/subscription-plan.model';
import { getSubscriptionPlan } from '@/services/subscription-plan.service';
import PageHeader from '@/components/PageHeader';
import SubscriptionCodeListPage from '../../code/list/subscription-code-list';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="subscription-plan-form">Plan Detail</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="subscription-redeem-codes">Redeem Codes</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;

  const [activeTab, setActiveTab] = useState(requestedTab || 'subscription-plan-form');
  const [formObject, setFormObject] = useState<SubscriptionPlan|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<SubscriptionPlan> = await getSubscriptionPlan(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Subscription Plan: ${errorModel?.errorMessage}`);
        nav('/plugin/subscription/plans');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Plan` : 'New Plan'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item > Subscription </Breadcrumb.Item>
            <Breadcrumb.Item href='/subscription/plans'> Plans</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'subscription-plan-form' && <SubscriptionPlanForm formObject={formObject} pageState={pageState}/>}
          {activeTab == 'subscription-redeem-codes' && <SubscriptionCodeListPage subscriptionPlanId={id} />}
        </>
      }
    </Panel>
  );
};

export default Page;
